import React from 'react';

let did = false;
const ensure = () => {
  if (did || typeof document === 'undefined') return;
  did = true;
  const s = document.createElement('style');
  s.dataset.pl = 'pageheader';
  s.textContent = `
.pl-phead{display:flex;flex-wrap:wrap;align-items:flex-end;justify-content:space-between;gap:14px 24px}
.pl-phead__lead{flex:1 1 auto;min-width:0;display:flex}
.pl-phead__text{min-width:0;display:flex;flex-direction:column;gap:var(--pl-s-4)}
.pl-phead__eyebrow{align-self:flex-start;max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.pl-phead__meta{align-self:flex-start;max-width:100%}
.pl-phead__back{position:relative;align-self:flex-start;display:inline-flex;align-items:center;gap:var(--pl-s-2);
  padding:0;border:0;background:none;cursor:pointer;color:var(--pl-ink-3);text-decoration:none;
  font-family:var(--pl-font-mono);font-size:var(--pl-fs-eyebrow);font-weight:var(--pl-w-medium);line-height:1.2;
  letter-spacing:var(--pl-track-label);text-transform:uppercase}
.pl-phead__back::after{content:'';position:absolute;left:calc(var(--pl-s-4) * -1);right:calc(var(--pl-s-4) * -1);
  bottom:calc(var(--pl-s-4) * -1);height:var(--pl-control-h)}
.pl-phead__back:hover{color:var(--pl-ink)}
.pl-phead__arrow{flex:none;display:block;width:10px;height:10px}
.pl-phead__sr{position:absolute;width:1px;height:1px;overflow:hidden;clip-path:inset(50%);white-space:nowrap}
.pl-phead__title{margin:0;color:var(--pl-ink);font-family:var(--pl-font-display);
  font-size:var(--pl-fs-h1-app);font-weight:var(--pl-w-semi);line-height:1.05;
  letter-spacing:var(--pl-track-display);text-wrap:balance}
.pl-phead__sub{max-width:72ch;margin:0;color:var(--pl-ink-2);font-size:var(--pl-fs-body);line-height:var(--pl-lh-snug)}
.pl-phead__actions{flex:0 1 auto;min-width:0;max-width:100%;display:flex;align-items:center;flex-wrap:wrap;gap:var(--pl-s-4)}
.pl-phead__unsaved{flex:none;color:var(--pl-ink-2)}
.pl-phead__primary{flex:none}
@media (max-width:767px){/* --pl-bp-tablet */
.pl-phead{gap:var(--pl-s-5) var(--pl-s-6)}
.pl-phead__actions{width:100%}
.pl-phead__actions>*{flex:1 1 auto}
.pl-phead__unsaved{flex:1 1 100%;order:-1}
.pl-phead__primary{flex:1 1 100%}
.pl-phead__primary>*{width:100%}}
@media (hover:none){
.pl-phead__back{padding:calc(var(--pl-touch-h) - 1.2em - var(--pl-s-4)) var(--pl-s-4) var(--pl-s-4);
  margin:calc(1.2em + var(--pl-s-4) - var(--pl-touch-h)) calc(var(--pl-s-4) * -1) calc(var(--pl-s-4) * -1)}
.pl-phead__back::after{content:none}}
`;
  document.head.appendChild(s);
};

/* An SVG, not U+2190: the arrow sits outside the self-hosted Plex subset and
   would otherwise fall back to whatever system font has it. */
const ARROW = (
  <svg className="pl-phead__arrow" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.4"
       aria-hidden="true" focusable="false">
    <path d="M9.5 5H1.2M4.6 1.6 1.2 5l3.4 3.4" />
  </svg>
);

const has = (v) => v !== undefined && v !== null && v !== '' && v !== false;

export function PageHeader({ eyebrow, meta, title, back, actions, primary, unsaved = false,
                             icon: _icon, tint: _tint, subtitle, className = '', ...rest }) {
  ensure();
  /* A list page's eyebrow sits above the H1. A detail page's back link takes
     that spot, so its eyebrow drops below the H1 as the meta line. */
  const above = !back && has(eyebrow) ? eyebrow : undefined;
  const below = has(meta) ? meta : (back && has(eyebrow) ? eyebrow : undefined);
  /* Deprecated `subtitle`: old call sites pass a sentence, so it reads as one,
     under the H1, rather than being forced into the uppercase eyebrow. */
  const sub = eyebrow === undefined && has(subtitle) ? subtitle : undefined;
  const hasActions = !!(actions || primary || unsaved);
  const backInner = (
    <React.Fragment>
      {ARROW}<span><span className="pl-phead__sr">Back to </span>{back ? back.label : null}</span>
    </React.Fragment>
  );
  return (
    <header className={['pl-phead', className].filter(Boolean).join(' ')} {...rest}>
      <div className="pl-phead__lead">
        <div className="pl-phead__text">
          {back ? (
            back.to ? (
              <a className="pl-phead__back" href={back.to} onClick={back.onClick}>{backInner}</a>
            ) : (
              <button type="button" className="pl-phead__back" onClick={back.onClick}>{backInner}</button>
            )
          ) : above !== undefined ? (
            <span className="pl-phead__eyebrow pl-label"
                  title={typeof above === 'string' ? above : undefined}>{above}</span>
          ) : null}
          <h1 className="pl-phead__title">{title}</h1>
          {below !== undefined ? <span className="pl-phead__meta pl-label">{below}</span> : null}
          {sub !== undefined ? <p className="pl-phead__sub">{sub}</p> : null}
        </div>
      </div>
      {hasActions ? (
        <div className="pl-phead__actions">
          {unsaved ? <span className="pl-phead__unsaved pl-label">Unsaved</span> : null}
          {actions}
          {primary ? <span className="pl-phead__primary">{primary}</span> : null}
        </div>
      ) : null}
    </header>
  );
}
